import { db } from "../firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp, deleteField } from "firebase/firestore";
import { User } from "firebase/auth";
import { fetchCommentsByUserId } from "./api";

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null | undefined;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  bio?: string;
  website?: string;
  github?: string;
  twitter?: string;
  linkedin?: string;
  openaiApiKey?: string; // User's personal OpenAI API key (private, only readable by user)
}

/**
 * Get user profile from Firestore
 * @param uid - User ID
 * @param includeApiKey - Whether to include the API key (only for own profile)
 */
export const getUserProfile = async (uid: string, includeApiKey: boolean = false): Promise<UserProfile | null> => {
  try {
    const profileRef = doc(db, "userProfiles", uid);
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      const data = profileSnap.data() as UserProfile;
      // Remove API key from response unless explicitly requested (for security)
      if (!includeApiKey && data.openaiApiKey) {
        const { openaiApiKey, ...profileWithoutKey } = data;
        return profileWithoutKey as UserProfile;
      }
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
};

/**
 * Sync user profile from Firebase Auth to Firestore
 * Creates profile if it doesn't exist, updates if it does
 * Preserves custom displayName and photoURL if they exist
 * Uses the oldest available date (Auth creation time or oldest comment) for createdAt
 */
export const syncUserProfile = async (user: User): Promise<UserProfile | null> => {
  try {
    const profileRef = doc(db, "userProfiles", user.uid);
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      // Profile exists - only update email and photoURL if they changed
      // Preserve custom displayName, bio, website
      // Fix createdAt if it's newer than the actual account creation date
      const existingProfile = profileSnap.data() as UserProfile;
      const updateData: Partial<UserProfile> = {
        email: user.email || "",
        updatedAt: serverTimestamp() as any,
      };

      // Check if we should fix the createdAt date (only if existing date is newer than actual account creation)
      let oldestDate: Timestamp | null = null;

      // 1. Check Firebase Auth metadata.creationTime
      if (user.metadata?.creationTime) {
        const authCreationDate = new Date(user.metadata.creationTime);
        oldestDate = Timestamp.fromDate(authCreationDate);
      }

      // 2. Check for comments to find the oldest activity date
      try {
        const comments = await fetchCommentsByUserId(user.uid);
        if (comments.length > 0) {
          const oldestComment = comments[comments.length - 1];
          if (oldestComment.timestamp) {
            const commentDate = oldestComment.timestamp;
            if (!oldestDate || commentDate.toMillis() < oldestDate.toMillis()) {
              oldestDate = commentDate;
            }
          }
        }
      } catch (commentError) {
        // If we can't fetch comments, continue with Auth creation time
        console.warn("Could not fetch comments for profile creation date:", commentError);
      }

      // Only update createdAt if we found an older date than what's currently stored
      if (oldestDate && existingProfile.createdAt) {
        // createdAt is already a Timestamp type from the interface
        const existingCreatedAt = existingProfile.createdAt;
        
        if (oldestDate.toMillis() < existingCreatedAt.toMillis()) {
          // Found an older date - update createdAt to use the oldest available date
          updateData.createdAt = oldestDate;
        }
      }

      // Only update photoURL if it's different and user hasn't set a custom one
      // If user has a custom photoURL (stored in Firebase Storage), don't overwrite it
      const currentPhotoURL = existingProfile.photoURL;
      const googlePhotoURL = user.photoURL || null;

      // Update photoURL only if:
      // 1. User doesn't have a custom photoURL (not from Firebase Storage)
      // 2. Google photoURL is different from current
      if (!currentPhotoURL ||
        (currentPhotoURL === googlePhotoURL && googlePhotoURL !== null) ||
        (currentPhotoURL.includes("googleusercontent.com") && googlePhotoURL !== currentPhotoURL)) {
        updateData.photoURL = googlePhotoURL;
      }

      await updateDoc(profileRef, updateData);
      const updatedSnap = await getDoc(profileRef);
      return updatedSnap.data() as UserProfile;
    } else {
      // Create new profile - use the oldest available date for createdAt
      let oldestDate: Timestamp | null = null;

      // 1. Check Firebase Auth metadata.creationTime (actual account creation)
      if (user.metadata?.creationTime) {
        const authCreationDate = new Date(user.metadata.creationTime);
        oldestDate = Timestamp.fromDate(authCreationDate);
      }

      // 2. Check for comments to find the oldest activity date
      try {
        const comments = await fetchCommentsByUserId(user.uid);
        if (comments.length > 0) {
          // Comments are ordered by timestamp desc, so the last one is the oldest
          const oldestComment = comments[comments.length - 1];
          if (oldestComment.timestamp) {
            // timestamp is already a Timestamp type
            const commentDate = oldestComment.timestamp;
            
            // Use the oldest date available
            if (!oldestDate || commentDate.toMillis() < oldestDate.toMillis()) {
              oldestDate = commentDate;
            }
          }
        }
      } catch (commentError) {
        // If we can't fetch comments, continue with Auth creation time
        console.warn("Could not fetch comments for profile creation date:", commentError);
      }

      // Use the oldest date found, or fall back to serverTimestamp if none found
      const createdAt = oldestDate || (serverTimestamp() as any);

      const profileData: Partial<UserProfile> = {
        uid: user.uid,
        email: user.email || "",
        displayName: user.displayName || user.email?.split("@")[0] || "User",
        photoURL: user.photoURL || null,
        createdAt: createdAt,
        updatedAt: serverTimestamp() as any,
      };

      const newProfile: UserProfile = profileData as UserProfile;
      await setDoc(profileRef, newProfile);
      return newProfile;
    }
  } catch (error) {
    console.error("Error syncing user profile:", error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  uid: string,
  updates: Partial<Pick<UserProfile, "displayName" | "bio" | "website" | "photoURL" | "github" | "twitter" | "linkedin" | "openaiApiKey">>
): Promise<boolean> => {
  try {
    const profileRef = doc(db, "userProfiles", uid);

    // Filter out undefined values and convert empty strings to deleteField for optional fields
    const cleanUpdates: any = {
      updatedAt: serverTimestamp(),
    };

    // Handle required field
    if (updates.displayName !== undefined) {
      cleanUpdates.displayName = updates.displayName;
    }

    // Handle optional fields - if undefined, omit; if empty string, delete the field
    const optionalFields: Array<keyof Pick<UserProfile, "bio" | "website" | "photoURL" | "github" | "twitter" | "linkedin" | "openaiApiKey">> =
      ["bio", "website", "photoURL", "github", "twitter", "linkedin", "openaiApiKey"];

    optionalFields.forEach((field) => {
      if (field in updates) {
        const value = updates[field];
        if (value === undefined) {
          // Omit undefined values
          return;
        } else if (value === "" || value === null) {
          // Delete the field if it's empty or null
          cleanUpdates[field] = deleteField();
        } else {
          // Set the value
          cleanUpdates[field] = value;
        }
      }
    });

    await updateDoc(profileRef, cleanUpdates);
    return true;
  } catch (error) {
    console.error("Error updating user profile:", error);
    return false;
  }
};

/**
 * Get user's photoURL from profile, fallback to null
 */
export const getUserPhotoURL = async (uid: string): Promise<string | null> => {
  const profile = await getUserProfile(uid);
  return profile?.photoURL || null;
};
